import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Save, CheckCircle, Search, RotateCcw } from 'lucide-react';
import { translationsApi } from '@/services/api';
import { toast } from 'sonner';
import i18n from '@/i18n';
import enDefaults from '@/i18n/en.json';
import arDefaults from '@/i18n/ar.json';

// Flatten a nested translation object into { "a.b.c": "value" } pairs (string leaves only).
const flatten = (obj, prefix = '', out = {}) => {
  Object.entries(obj || {}).forEach(([k, v]) => {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) flatten(v, key, out);
    else out[key] = v;
  });
  return out;
};

// Rebuild a nested object from flat { "a.b.c": "value" } pairs.
const unflatten = (flat) => {
  const out = {};
  Object.entries(flat).forEach(([key, val]) => {
    const parts = key.split('.');
    let cur = out;
    parts.forEach((p, i) => {
      if (i === parts.length - 1) cur[p] = val;
      else {
        cur[p] = cur[p] || {};
        cur = cur[p];
      }
    });
  });
  return out;
};

const flatEnDefaults = flatten(enDefaults);
const flatArDefaults = flatten(arDefaults);
// The union of keys across both languages, so nothing is missed.
const ALL_KEYS = Array.from(new Set([...Object.keys(flatEnDefaults), ...Object.keys(flatArDefaults)]));

// Friendly section titles for the top-level i18n groups.
const GROUP_LABELS = {
  nav: 'Navigation',
  hero: 'Hero',
  about: 'About Section',
  process: 'Process',
  projects: 'Projects',
  whyUs: 'Why Choose Us',
  video: 'Video',
  testimonials: 'Testimonials',
  stats: 'Stats',
  blog: 'Blog',
  contact: 'Contact',
  footer: 'Footer',
  beforeAfter: 'Before / After',
  orderJourney: 'Order Journey',
  order: 'Order Flow',
};

const AdminContent = () => {
  const [enVals, setEnVals] = useState({});
  const [arVals, setArVals] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    translationsApi.get()
      .then((res) => {
        const ovEn = flatten(res.data?.en || {});
        const ovAr = flatten(res.data?.ar || {});
        const en = {};
        const ar = {};
        ALL_KEYS.forEach((k) => {
          en[k] = ovEn[k] !== undefined ? ovEn[k] : (flatEnDefaults[k] || '');
          ar[k] = ovAr[k] !== undefined ? ovAr[k] : (flatArDefaults[k] || '');
        });
        setEnVals(en);
        setArVals(ar);
      })
      .catch(() => toast.error('Failed to load content'))
      .finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const byGroup = {};
    ALL_KEYS.forEach((key) => {
      if (q) {
        const hay = `${key} ${enVals[key] || ''} ${arVals[key] || ''}`.toLowerCase();
        if (!hay.includes(q)) return;
      }
      const group = key.split('.')[0];
      (byGroup[group] = byGroup[group] || []).push(key);
    });
    return byGroup;
  }, [query, enVals, arVals]);

  const resetField = (key) => {
    setEnVals((s) => ({ ...s, [key]: flatEnDefaults[key] || '' }));
    setArVals((s) => ({ ...s, [key]: flatArDefaults[key] || '' }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    // Only persist values that differ from the bundled defaults, so untouched
    // text keeps tracking the site defaults.
    const enDiff = {};
    const arDiff = {};
    ALL_KEYS.forEach((k) => {
      if (enVals[k] !== undefined && enVals[k] !== (flatEnDefaults[k] || '')) enDiff[k] = enVals[k];
      if (arVals[k] !== undefined && arVals[k] !== (flatArDefaults[k] || '')) arDiff[k] = arVals[k];
    });
    const enTree = unflatten(enDiff);
    const arTree = unflatten(arDiff);
    try {
      await translationsApi.update({ en: enTree, ar: arTree });
      // Reflect immediately in the admin's own i18n instance.
      if (Object.keys(enTree).length) i18n.addResourceBundle('en', 'translation', enTree, true, true);
      if (Object.keys(arTree).length) i18n.addResourceBundle('ar', 'translation', arTree, true, true);
      i18n.changeLanguage(i18n.language);
      setSaved(true);
      toast.success('Content saved');
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const isEdited = (key) =>
    enVals[key] !== (flatEnDefaults[key] || '') || arVals[key] !== (flatArDefaults[key] || '');

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-white/5 w-48" />
        <div className="h-64 bg-white/5" />
      </div>
    );
  }

  const groupNames = Object.keys(groups);

  return (
    <div data-testid="admin-content">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Site Content</h1>
        <p className="text-white/60">
          Edit any text on the website in English and Arabic. Blank the Arabic field to reuse the English text.
          Untouched fields keep the site defaults.
        </p>
      </div>

      <div className="sticky top-0 z-10 bg-brand-dark/95 backdrop-blur py-3 mb-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search content by text or key..."
            className="w-full bg-white/5 border border-white/10 text-white pl-9 pr-4 py-2.5 focus:border-gold outline-none"
          />
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          data-testid="save-content-btn"
          className={`flex items-center justify-center gap-2 px-6 py-2.5 font-medium transition-colors ${
            saved ? 'bg-green-500 text-white' : 'bg-gold text-charcoal hover:bg-gold/90'
          }`}
        >
          {saving ? (
            <span className="w-5 h-5 border-2 border-charcoal border-t-transparent rounded-full animate-spin" />
          ) : saved ? (
            <>
              <CheckCircle size={18} /> Saved
            </>
          ) : (
            <>
              <Save size={18} /> Save Content
            </>
          )}
        </button>
      </div>

      {groupNames.length === 0 && (
        <p className="text-white/50 py-8 text-center">No content matches “{query}”.</p>
      )}

      <div className="space-y-4">
        {groupNames.map((group) => (
          <details key={group} open={!!query} className="bg-white/5 border border-white/10">
            <summary className="cursor-pointer select-none px-5 py-4 text-white font-medium flex items-center justify-between">
              <span>{GROUP_LABELS[group] || group}</span>
              <span className="text-white/40 text-sm">{groups[group].length}</span>
            </summary>
            <div className="px-5 pb-5 space-y-5 border-t border-white/10 pt-4">
              {groups[group].map((key) => (
                <div key={key} data-testid={`content-field-${key}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-white/40 text-xs font-mono">{key}</label>
                    {isEdited(key) && (
                      <button
                        type="button"
                        onClick={() => resetField(key)}
                        className="flex items-center gap-1 text-white/40 hover:text-gold text-xs"
                        title="Reset to default"
                      >
                        <RotateCcw size={12} /> reset
                      </button>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <span className="block text-white/50 text-xs mb-1">English</span>
                      <textarea
                        rows={1}
                        value={enVals[key] ?? ''}
                        onChange={(e) => setEnVals((s) => ({ ...s, [key]: e.target.value }))}
                        className="w-full bg-white/5 border border-white/10 text-white px-3 py-2 focus:border-gold outline-none resize-y text-sm"
                      />
                    </div>
                    <div>
                      <span className="block text-white/50 text-xs mb-1">Arabic</span>
                      <textarea
                        rows={1}
                        value={arVals[key] ?? ''}
                        onChange={(e) => setArVals((s) => ({ ...s, [key]: e.target.value }))}
                        dir="rtl"
                        className="w-full bg-white/5 border border-white/10 text-white px-3 py-2 focus:border-gold outline-none resize-y text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>

      <div className="mt-8">
        <motion.button
          type="button"
          onClick={handleSave}
          disabled={saving}
          whileTap={{ scale: 0.98 }}
          className={`flex items-center justify-center gap-2 w-full py-4 font-medium transition-colors ${
            saved ? 'bg-green-500 text-white' : 'bg-gold text-charcoal hover:bg-gold/90'
          }`}
        >
          {saving ? (
            <span className="w-5 h-5 border-2 border-charcoal border-t-transparent rounded-full animate-spin" />
          ) : saved ? (
            <>
              <CheckCircle size={20} /> Content Saved
            </>
          ) : (
            <>
              <Save size={20} /> Save Content
            </>
          )}
        </motion.button>
      </div>
    </div>
  );
};

export default AdminContent;
